import * as Toggle from '@radix-ui/react-toggle-group'
import { useKestrel } from 'features/kestrelViewer'
import { DeleteAnnotationButton } from 'features/kestrelViewer/kestrelViewerMarkup/annotations/DeleteAnnotationButton'
import { EditAnnotationButton } from 'features/kestrelViewer/kestrelViewerMarkup/annotations/EditAnnotationButton'
import { SeverityCircle } from 'features/kestrelViewer/kestrelViewerMarkup/annotations/SeverityCircle'
import { ToggleAnnotationVisibilityButton } from 'features/kestrelViewer/kestrelViewerMarkup/annotations/ToggleAnnotationVisibilityButton'
import { ZoomToAnnotationButton } from 'features/kestrelViewer/kestrelViewerMarkup/annotations/ZoomToAnnotationButton'
import {
  ActionsContainer,
  DraggableWrapper,
  DropIndicator,
  LeftSideInfo,
  PrimaryLineContainer,
} from 'features/kestrelViewer/kestrelViewerMarkup/utils/DragAndDropComponents'
import { KestrelCommands } from 'features/kestrelViewer/types/KestrelCommands'
import React, { FC, useState } from 'react'
import styled from 'styled-components'
import { Vector2 } from 'three'
import { KestrelInspectionType, KVAnnotation } from 'types'

const StyledAnnotationEntry = styled(Toggle.Item)<{
  $selected: boolean
  $indented?: boolean
}>`
  all: unset;
  cursor: ${({ $selected }) => ($selected ? 'auto' : 'pointer')} !important;
  background-color: ${({ theme, $selected }) =>
    $selected ? theme.forecast.background4 : ''} !important;
  position: relative;
  padding: 4px;
  margin-left: ${({ $indented }) => ($indented ? '1rem' : '0')};

  &:hover {
    opacity: ${({ $selected }) => ($selected ? 0.8 : 1)} !important;
    background-color: ${({ theme, $selected }) =>
      $selected
        ? theme.forecast.background4
        : theme.forecast.background3} !important;
  }
`

const AnnotationLabel = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
`

export const AnnotationEntry: FC<{
  annotation: KVAnnotation
  selectedMarkupId: string | undefined
  isMultiSelected?: boolean
  onMultiSelectClick?: (isShiftClick: boolean) => void
  selectedAnnotationIds?: Set<string>
  onReorder?: (
    draggedIds: string[],
    targetId: string,
    position: 'before' | 'after'
  ) => void
}> = ({
  annotation,
  selectedMarkupId,
  isMultiSelected = false,
  onMultiSelectClick,
  selectedAnnotationIds,
  onReorder,
}) => {
  const isSelected = annotation.id === selectedMarkupId
  const [isHovered, setIsHovered] = useState(false)
  const [dropPosition, setDropPosition] = useState<'before' | 'after' | null>(
    null
  )
  const {
    setPopupMenuStartPosition,
    setSelectedMarkup,
    sendKVCommand,
    annotationSortKey,
    derivedTags,
    inspectionType,
  } = useKestrel()

  // Historical annotations should not be updated by the user in the new scan
  const disabled =
    annotation.historical || derivedTags.includes(annotationSortKey)

  // Custom drag handlers for multi-select functionality
  const handleDragStart = (e: React.DragEvent) => {
    e.stopPropagation()

    // If dragging a selected annotation, drag all selected annotations
    const annotationsToDrag =
      isMultiSelected && selectedAnnotationIds
        ? Array.from(selectedAnnotationIds)
        : [annotation.id]

    e.dataTransfer.setData('annotationIds', JSON.stringify(annotationsToDrag))
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (!onReorder) return

    // Only handle if dragging annotations
    if (!e.dataTransfer.types.includes('annotationids')) {
      return
    }

    // Determine if we should drop before or after based on mouse position
    const rect = e.currentTarget.getBoundingClientRect()
    const mouseY = e.clientY - rect.top
    const position = mouseY < rect.height / 2 ? 'before' : 'after'

    setDropPosition(position)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDropPosition(null)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()

    const annotationIds = e.dataTransfer.getData('annotationIds')
    if (annotationIds && onReorder && dropPosition) {
      const ids = JSON.parse(annotationIds)
      // Don't reorder if dropping on itself
      if (!ids.includes(annotation.id)) {
        onReorder(ids, annotation.id, dropPosition)
      }
    }

    setDropPosition(null)
  }

  const handleDragEnd = () => {
    setDropPosition(null)
  }

  // Helper function to get the correct highlight command based on annotation type
  const getHighlightCommand = (isHover = false) => {
    if (annotation.geometry?.kind === 'polygon') {
      return {
        command: isHover
          ? KestrelCommands.HIGHLIGHT_SURFACE_AREA
          : KestrelCommands.HIGHLIGHT_SURFACE_AREA,
        paramName: 'surfaceAreaId',
        unhighlightCommand: KestrelCommands.UNHIGHLIGHT_ALL_SURFACE_AREAS,
      }
    } else {
      return {
        command: isHover
          ? KestrelCommands.HIGHLIGHT_ANNOTATION
          : KestrelCommands.HIGHLIGHT_ANNOTATION,
        paramName: 'annotationId',
        unhighlightCommand: KestrelCommands.UNHIGHLIGHT_ALL_ANNOTATIONS,
      }
    }
  }

  const handleEntryClick = (e: React.MouseEvent) => {
    // Check for multi-select click (ctrl/cmd or shift)
    if ((e.ctrlKey || e.metaKey || e.shiftKey) && onMultiSelectClick) {
      e.stopPropagation()
      onMultiSelectClick(e.shiftKey)
      return
    }

    e.stopPropagation()
    if (setPopupMenuStartPosition) {
      setPopupMenuStartPosition(new Vector2(80, 20))
    }
    const newSelectedMarkup =
      selectedMarkupId === annotation.id ? undefined : annotation
    setSelectedMarkup(newSelectedMarkup)

    // Send command to Kestrel to select/deselect the annotation
    if (sendKVCommand) {
      const { command, paramName } = getHighlightCommand()
      sendKVCommand({
        command,
        [paramName]: newSelectedMarkup?.id || '',
      })
    }
  }

  const handleHover = () => {
    if (sendKVCommand && !selectedMarkupId) {
      const { command, paramName } = getHighlightCommand(true)
      sendKVCommand({
        command,
        [paramName]: annotation.id,
      })
    }
  }

  const handleHoverEnd = () => {
    if (sendKVCommand && !selectedMarkupId) {
      const { unhighlightCommand } = getHighlightCommand()
      sendKVCommand({
        command: unhighlightCommand,
      })
    }
  }

  return (
    <StyledAnnotationEntry
      value={annotation.id}
      $selected={isSelected}
      $indented={
        !!annotation.tags[annotationSortKey] &&
        !!annotation.tags[annotationSortKey].value
      }
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={{
        backgroundColor: isMultiSelected ? 'rgba(76, 175, 80, 0.2)' : undefined,
        borderLeft: isMultiSelected ? '3px solid #4CAF50' : undefined,
      }}
      onClick={handleEntryClick}
      onMouseEnter={() => {
        setIsHovered(true)
        handleHover()
      }}
      onMouseLeave={() => {
        setIsHovered(false)
        handleHoverEnd()
      }}>
      {dropPosition && <DropIndicator $position={dropPosition} />}
      <DraggableWrapper
        $isDragging={false}
        draggable={!disabled}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        style={{ cursor: disabled ? 'default' : 'grab' }}>
        <PrimaryLineContainer $visible={annotation.visible}>
          <LeftSideInfo>
            <SeverityCircle
              annotation={annotation}
              severity={
                inspectionType === KestrelInspectionType.NAVY
                  ? annotation.navyAnnotation?.navySeverity ||
                    annotation.severity
                  : annotation.severity
              }
            />
            <AnnotationLabel>
              {inspectionType === KestrelInspectionType.G1N6 &&
              annotation.g1n6Annotation?.damageCategory
                ? annotation.g1n6Annotation.damageCategory
                : annotation.label}
            </AnnotationLabel>
          </LeftSideInfo>
          {!isHovered &&
            inspectionType !== KestrelInspectionType.ROOFTOP &&
            annotation.type}{' '}
          {isHovered && (
            <ActionsContainer>
              <EditAnnotationButton annotation={annotation} />
              <ZoomToAnnotationButton annotation={annotation} />
              <ToggleAnnotationVisibilityButton annotation={annotation} />
              {!annotation.historical && (
                <DeleteAnnotationButton annotationId={annotation.id} />
              )}
            </ActionsContainer>
          )}
        </PrimaryLineContainer>
        {isSelected && (
          <div style={{ color: 'lightgray', fontStyle: 'italic' }}>
            {annotation.notes}
          </div>
        )}
      </DraggableWrapper>
    </StyledAnnotationEntry>
  )
}
